import { z } from 'zod';
import { uuidSchema } from './common.js';
/**
 * The intelligence contract.
 *
 * Every suggestion that crosses this boundary carries its evidence. That is
 * not decoration: a bookkeeper who cannot interrogate a suggestion will either
 * accept it blindly or ignore it entirely, and both are failures. So there is
 * no shape here that says "category X, 98%" without also saying how many
 * records that came from, out of how many, and when they were.
 */
export const suggestionEvidenceSchema = z.object({
    /** How many confirmed records carried this value. */
    observed: z.number().int(),
    /** Out of how many confirmed records for this supplier. */
    of: z.number().int(),
    /** How many times a human moved a record off it. */
    correctedAway: z.number().int(),
    share: z.number().nullable(),
    firstObservedOn: z.string().nullable(),
    lastObservedOn: z.string().nullable(),
    amountTotalMinor: z.string().nullable(),
    basis: z.string(),
});
export const suggestionSchema = z.object({
    /** The suggested value: a category id, a classification, 'yes' or 'no'. */
    value: z.string(),
    /** What to show a person instead of an id. */
    label: z.string().nullable(),
    /**
     * A Wilson lower bound, not a raw share. Two-of-two is not certainty, and a
     * raw proportion is exactly how a system auto-applies something it saw once.
     */
    confidence: z.number(),
    evidence: suggestionEvidenceSchema,
});
export const ANOMALY_CODES = [
    'tax_missing',
    'tax_rate_unusual',
    'amount_unusual',
    'category_new_for_vendor',
];
export const anomalyCodeSchema = z.enum(ANOMALY_CODES);
export const ANOMALY_LABELS = {
    tax_missing: 'No HST on a supplier that usually charges it',
    tax_rate_unusual: 'An unusual tax rate for this supplier',
    amount_unusual: 'An unusual amount for this supplier',
    category_new_for_vendor: 'A new account for this supplier',
};
export const anomalySchema = z.object({
    code: anomalyCodeSchema,
    /** Written as a sentence with real numbers in it, by the database. */
    message: z.string(),
});
export const duplicateCandidateSchema = z.object({
    transactionId: uuidSchema,
    transactionDate: z.string(),
    grossAmountMinor: z.string(),
    reference: z.string().nullable(),
    sourceDocumentId: uuidSchema.nullable(),
    reason: z.string(),
});
/**
 * A shortlist, deliberately not a percentage.
 *
 * A receipt does not know which job it belongs to. What the system knows is
 * which jobs were open that day, which of them this supplier has been billed
 * to before, and which are for the customer already on the record. Those go on
 * the screen as reasons; a confident-looking number attached to a guess would
 * be worse than an honest list.
 */
export const jobSuggestionSchema = z.object({
    jobId: uuidSchema,
    jobNumber: z.string().nullable(),
    title: z.string(),
    customerName: z.string().nullable(),
    score: z.number().int(),
    reasons: z.array(z.string()),
});
export const recordInsightsSchema = z.object({
    category: suggestionSchema.nullable(),
    classification: suggestionSchema.nullable(),
    anomalies: z.array(anomalySchema),
    duplicates: z.array(duplicateCandidateSchema),
    jobs: z.array(jobSuggestionSchema),
});
/* -------------------------------------------------------------------------- */
/* A supplier's memory                                                        */
/* -------------------------------------------------------------------------- */
export const PROFILE_DIMENSIONS = [
    'category',
    'classification',
    'cca_class',
    'job_linked',
    'tax_present',
];
export const profileDimensionSchema = z.enum(PROFILE_DIMENSIONS);
export const PROFILE_DIMENSION_LABELS = {
    category: 'Coded to',
    classification: 'Treated as',
    cca_class: 'CCA class',
    job_linked: 'Linked to a job',
    tax_present: 'Charged HST',
};
export const vendorObservationSchema = z.object({
    dimension: profileDimensionSchema,
    valueKey: z.string(),
    label: z.string().nullable(),
    observationCount: z.number().int(),
    correctedAwayCount: z.number().int(),
    firstObservedOn: z.string().nullable(),
    lastObservedOn: z.string().nullable(),
});
export const vendorTimelineEntrySchema = z.object({
    periodYear: z.number().int(),
    recordCount: z.number().int(),
    totalAmountMinor: z.string(),
    topCategory: z.string().nullable(),
    topCategoryCount: z.number().int().nullable(),
});
export const vendorIntelligenceSchema = z.object({
    vendorId: uuidSchema,
    displayName: z.string(),
    recordCount: z.number().int(),
    firstRecordedOn: z.string().nullable(),
    lastRecordedOn: z.string().nullable(),
    totalAmountMinor: z.string().nullable(),
    meanAmountMinor: z.string().nullable(),
    maxAmountMinor: z.string().nullable(),
    taxPresentCount: z.number().int().nullable(),
    taxAbsentCount: z.number().int().nullable(),
    /** Basis points: 1500 is 15%, which is New Brunswick HST. */
    meanTaxRateBp: z.number().int().nullable(),
    aliasCount: z.number().int(),
    observations: z.array(vendorObservationSchema),
    timeline: z.array(vendorTimelineEntrySchema),
});
export const intelligenceSettingsSchema = z.object({
    autoApplyEnabled: z.boolean(),
    autoApplyThreshold: z.number(),
    autoApplyMinObservations: z.number().int(),
});
export const updateIntelligenceSettingsSchema = z.strictObject({
    autoApplyEnabled: z.boolean().optional(),
    autoApplyThreshold: z.number().min(0.5).max(1).optional(),
    autoApplyMinObservations: z.number().int().min(1).max(10_000).optional(),
});
/** Where the engine's stated confidence is compared with what happened. */
export const calibrationSchema = z.object({
    suggestionsMade: z.number().int(),
    suggestionsApplied: z.number().int(),
    /** Of the applied suggestions, how many a human later moved off. */
    overriddenAfterApply: z.number().int(),
    meanStatedConfidence: z.number().nullable(),
    observedAccuracy: z.number().nullable(),
    vendorsWithHistory: z.number().int(),
    aliasesLearned: z.number().int(),
});
/** A percentage for a screen, from a basis-point integer. */
export function formatRateBp(basisPoints) {
    if (basisPoints === null)
        return 'not observed';
    return `${(basisPoints / 100).toFixed(2).replace(/\.00$/, '')}%`;
}
/**
 * How sure, in words.
 *
 * A number alone invites false precision: 0.82 and 0.86 are the same decision.
 * The words are what a bookkeeper acts on; the number stays visible beside it
 * for anyone who wants it.
 */
export function confidenceLabel(confidence) {
    if (confidence >= 0.95)
        return 'Very confident';
    if (confidence >= 0.8)
        return 'Confident';
    if (confidence >= 0.6)
        return 'Likely';
    if (confidence >= 0.3)
        return 'A guess worth checking';
    return 'Not enough history';
}
//# sourceMappingURL=intelligence.js.map